import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/privacy")({
  component: PrivacyPage,
  head: () => ({
    meta: [
      { title: "Privacy Policy | Spirit Science Summit" },
      {
        name: "description",
        content:
          "Privacy Policy for the Spirit Science Summit website and event.",
      },
    ],
  }),
});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="text-xl font-semibold text-foreground">{title}</h2>
      <div className="mt-3 space-y-3 text-muted-foreground leading-relaxed">{children}</div>
    </section>
  );
}

function PrivacyPage() {
  return (
    <main className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-6 py-16">
        <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
          ← Back to home
        </Link>

        <h1 className="mt-6 text-4xl font-bold text-foreground">Privacy Policy</h1>

        <div className="mt-6 space-y-1 text-sm text-muted-foreground">
          <p><span className="text-foreground font-medium">Effective Date:</span> May 21, 2026</p>
          <p><span className="text-foreground font-medium">Website:</span> spiritsciencesummit.com</p>
          <p>
            <span className="text-foreground font-medium">Contact:</span>{" "}
            <a href="mailto:info@spiritsciencesummit.com" className="underline hover:text-foreground">
              info@spiritsciencesummit.com
            </a>
          </p>
        </div>

        <Section title="1. Introduction">
          <p>
            This Privacy Policy explains how we collect, use, store, and protect information when
            you visit our website, purchase tickets, contact us, or interact with the Integrating
            Well-being, Spirituality, Spiritism & Science Summit.
          </p>
          <p>By using this website, you agree to the terms of this Privacy Policy.</p>
        </Section>

        <Section title="2. Information We May Collect">
          <p>We may collect personal information that you voluntarily provide, including:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Name</li>
            <li>Email address</li>
            <li>Phone number, if provided</li>
            <li>Ticket purchase information</li>
            <li>Dietary or accessibility requests, if submitted</li>
            <li>Questions or messages sent through email or contact forms</li>
            <li>Any other information you choose to provide</li>
          </ul>
          <p>
            When you purchase tickets, payment information may be processed by a third-party
            ticketing or payment provider. We do not store your full credit card or payment account
            information on our website.
          </p>
        </Section>

        <Section title="3. Information Collected Automatically">
          <p>When you visit the website, certain information may be collected automatically, such as:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>IP address</li>
            <li>Browser type</li>
            <li>Device type</li>
            <li>Pages visited</li>
            <li>Time spent on the website</li>
            <li>Referring website</li>
            <li>General location data based on IP address</li>
          </ul>
          <p>
            This information may be collected through cookies, analytics tools, or similar
            technologies.
          </p>
        </Section>

        <Section title="4. How We Use Your Information">
          <p>We may use your information to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Process ticket registrations</li>
            <li>Confirm event attendance</li>
            <li>Communicate event updates</li>
            <li>Respond to questions or requests</li>
            <li>Manage dietary, accessibility, or translation-related needs</li>
            <li>Improve the website and event experience</li>
            <li>Send relevant event communications</li>
            <li>Prevent fraud or misuse</li>
            <li>Comply with legal obligations</li>
          </ul>
        </Section>

        <Section title="5. Email Communications">
          <p>
            If you provide your email address, we may contact you with event-related information,
            including confirmations, reminders, updates, logistics, and important notices.
          </p>
          <p>
            We may also send occasional communications about related events or initiatives. You may
            unsubscribe or request removal from non-essential communications by contacting us at
            info@spiritsciencesummit.com.
          </p>
        </Section>

        <Section title="6. Sharing of Information">
          <p>We do not sell your personal information.</p>
          <p>
            We may share limited information with trusted service providers who help us operate the
            event and website, including:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Ticketing platforms</li>
            <li>Payment processors</li>
            <li>Email communication providers</li>
            <li>Website hosting providers</li>
            <li>Event staff or volunteers</li>
            <li>Catering or accessibility support providers, when necessary</li>
          </ul>
          <p>These parties are only provided the information needed to perform their services.</p>
          <p>
            We may also disclose information if required by law, legal process, public safety
            concerns, or to protect our rights and the rights of others.
          </p>
        </Section>

        <Section title="7. Third-Party Websites and Services">
          <p>
            This website may contain links to third-party websites, including ticketing platforms,
            payment providers, maps, speaker websites, or other external resources.
          </p>
          <p>
            We are not responsible for the privacy practices, content, or policies of third-party
            websites. Please review their privacy policies before submitting personal information.
          </p>
        </Section>

        <Section title="8. Cookies and Analytics">
          <p>
            Our website may use cookies or similar technologies to improve functionality, understand
            visitor behavior, and measure website performance.
          </p>
          <p>
            You can adjust your browser settings to refuse cookies or alert you when cookies are
            being used. Some website features may not function properly if cookies are disabled.
          </p>
        </Section>

        <Section title="9. Data Security">
          <p>
            We take reasonable steps to protect your personal information from unauthorized access,
            misuse, loss, or disclosure.
          </p>
          <p>
            However, no website, email system, or online transmission is completely secure. You
            provide information at your own risk.
          </p>
        </Section>

        <Section title="10. Data Retention">
          <p>
            We retain personal information only as long as reasonably necessary for event
            administration, legal compliance, accounting, dispute resolution, and communication
            purposes.
          </p>
          <p>
            After the information is no longer needed, we may delete, anonymize, or securely retain
            it according to our operational and legal requirements.
          </p>
        </Section>

        <Section title="11. Children's Privacy">
          <p>
            This website and event are intended for a general audience and are not directed to
            children under 13 years of age.
          </p>
          <p>
            We do not knowingly collect personal information from children under 13. If you believe
            a child has provided personal information, please contact us so we can delete it.
          </p>
        </Section>

        <Section title="12. Your Privacy Choices">
          <p>You may contact us to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Request access to personal information we may have about you</li>
            <li>Correct inaccurate information</li>
            <li>Request deletion of your information, when legally permissible</li>
            <li>Opt out of non-essential email communications</li>
            <li>Ask questions about how your information is used</li>
          </ul>
          <p>Please contact us at:</p>
          <p>
            <a href="mailto:info@spiritsciencesummit.com" className="underline hover:text-foreground">
              info@spiritsciencesummit.com
            </a>
          </p>
        </Section>

        <Section title="13. International Visitors">
          <p>
            If you access this website from outside the United States, you understand that your
            information may be processed and stored in the United States, where privacy laws may
            differ from those in your country.
          </p>
        </Section>

        <Section title="14. Updates to This Privacy Policy">
          <p>
            We may update this Privacy Policy from time to time. Updates will be posted on this
            page with a revised effective date.
          </p>
          <p>
            Your continued use of the website after updates are posted means you accept the revised
            Privacy Policy.
          </p>
        </Section>

        <Section title="15. Contact">
          <p>For questions about this Privacy Policy, please contact:</p>
          <p>
            <a href="mailto:info@spiritsciencesummit.com" className="underline hover:text-foreground">
              info@spiritsciencesummit.com
            </a>
          </p>
        </Section>
      </div>
    </main>
  );
}
